import React, { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import {
  CustomerShippingOptionsState,
  ShippingRegionOption,
  regionName
} from "../../shared/models/customerShippingOptions";
import { getShippingOptions, setError, setSuccess } from "../customerShippingOptionsSlice";
import { Box, Button } from "@mui/material";
import colors from "../../../shared/themes/colors";
import ActionToast from "../../../shared/components/ActionToast";
import { NotificationMessage, ShippingOptionCode, compressWeekDays, shippingCode } from "../../../shared/models/models";
import PriceFormat from "../../../shared/components/formats/PriceFormat";
import Loader from "../../../shared/components/Loader";
import ShippingDialog from "./ShippingDialog";
import moment from "moment";
import useTranslation from "../../../shared/hooks/useTranslation";

interface ShippingRegionOptionProps {
  option: ShippingRegionOption;
  countryId: number;
}

const ShippingRegion = ({ option, countryId }: ShippingRegionOptionProps) => {
  const { translate } = useTranslation("settings");
  return (
    <Box sx={{ minWidth: "208px" }}>
      <Box className="text-14-bold" color="#333333">
        {regionName(option.regionCode, countryId)}
      </Box>
      <Box className="text-14-regular" color="#333333">
        <Box>
          {option.fromBusinessDays}-{option.toBusinessDays} {translate("businessDays")}
        </Box>
        <PriceFormat value={option.orderPrice?.value} />
      </Box>
    </Box>
  );
};

const Shipping = () => {
  const { shippingOptions, loading, success, error } = useAppSelector<CustomerShippingOptionsState>(
    (state: any) => state.customerShippingOptions
  );

  const dispatch = useAppDispatch();
  const { translate } = useTranslation("settings");

  const shippingCodes = [ShippingOptionCode.standart, ShippingOptionCode.express, ShippingOptionCode.selfPickUp];
  const [isShippingDialog, setIsShippingDialog] = useState(false);

  const handleEditShippingOptions = () => {
    setIsShippingDialog(true);
  };

  useEffect(() => {
    dispatch(getShippingOptions());
  }, []);

  return (
    <Box className="mt-2 h-full">
      <ActionToast
        open={!!success}
        variant="success"
        message={success || ""}
        onClose={() => dispatch(setSuccess(undefined))}
        duration={6000}
      />

      <ActionToast
        open={!!error}
        variant="error"
        message={NotificationMessage.error(error).message || ""}
        onClose={() => dispatch(setError(undefined))}
        duration={6000}
      />

      <Box className="flex items-center justify-between">
        <Box className="text-18-regular flex" color={"#333333"}>
          {translate("shippingSettingsInfo")}
        </Box>

        <Button variant="contained" onClick={handleEditShippingOptions}>
          {translate("editShippingOptions")}
        </Button>
      </Box>

      {loading ? (
        <Loader />
      ) : (
        <Box className="mt-6 flex flex-col gap-2">
          {shippingCodes.map((code) => (
            <Box
              key={code}
              className="flex gap-4 w-full py-2 px-4"
              sx={{ background: colors.primary.white2, minHeight: "100px", borderRadius: "4px" }}
            >
              <Box className="text-18-bold" color="#333333" sx={{ minWidth: "192px" }}>
                {code === ShippingOptionCode.selfPickUp ? translate("pickUp") : shippingCode(code)}
              </Box>

              {shippingOptions?.options &&
                shippingOptions?.options.filter((x) => x.typeCode === code).every((x) => !x.isActive) && (
                  <Box className="text-14-regular" color="#333333">
                    {translate("noActiveShipping", { shippingType: code === ShippingOptionCode.selfPickUp ? translate("pickUp") : shippingCode(code) })}
                    <br />
                    {translate("addEditShippingHint")},
                    <span className="text-14-bold" style={{ color: "#333333" }}>
                      {translate("editShippingOptions")}
                    </span>
                    .
                  </Box>
                )}

              {shippingOptions?.options
                .filter((x) => x.typeCode === code)
                .sort((x, y) => x.regionCode - y.regionCode)
                .map((option, i) => (
                  <Box key={i} sx={{ display: option.isActive ? "block" : "none" }}>
                    {option.isActive && (
                      <Box>
                        {option.typeCode !== 3 ? (
                          <ShippingRegion option={option} countryId={shippingOptions.countryId} />
                        ) : (
                          <>
                            {shippingOptions.warehouse && (
                              <Box>
                                <Box className="text-14-bold" color="#333333">
                                  {translate("warehouse")}
                                </Box>
                                <Box className="text-14-regular" color="#333333">
                                  {shippingOptions.warehouse?.address}<br />
                                  {compressWeekDays(shippingOptions?.warehouse.daysOfWeek || [])},{"\u00A0"}
                                  {moment(shippingOptions?.warehouse.opensAt, "HH:mm:ss").format("HH:mm")}-
                                  {moment(shippingOptions?.warehouse.closesAt, "HH:mm:ss").format("HH:mm")}
                                </Box>
                              </Box>
                            )}
                          </>
                        )}
                      </Box>
                    )}
                  </Box>
                ))}
            </Box>
          ))}
        </Box>
      )}

      <ShippingDialog
        key={`contact-dialog-${isShippingDialog}`}
        open={isShippingDialog}
        onClose={() => {
          setIsShippingDialog(false);
        }}
        shippingOptions={shippingOptions?.options}
        countryId={shippingOptions?.countryId || 0}
        warehouse={shippingOptions?.warehouse}
      />
    </Box>
  );
};
export default Shipping;